import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { authApi } from '@/api/auth.api';
import { SectionInvalidScreen } from './SectionInvalidScreen';
import { SectionForbiddenScreen } from './SectionForbiddenScreen';

type GuardState = 'loading' | 'valid' | 'unauthorized' | 'forbidden';

interface SectionGuardProps {
  children: React.ReactNode;
}

// Every protected route mounts its own guard, so without this the whole page
// would flash a spinner on each navigation. A recent successful check lets the
// page render at once while the account is re-checked in the background.
const RECENT_CHECK_MS = 5 * 60 * 1000;
let lastValidAt = 0;

export function SectionGuard({ children }: SectionGuardProps) {
  const { setUser, isAuthenticated, t } = useApp();
  const location = useLocation();
  const [state, setState] = useState<GuardState>(() =>
    isAuthenticated && Date.now() - lastValidAt < RECENT_CHECK_MS ? 'valid' : 'loading',
  );
  const abortRef = useRef<AbortController | null>(null);

  const validate = useCallback(async () => {
    if (!isAuthenticated) {
      setState('unauthorized');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const freshUser = await authApi.getCurrentUser();

      if (controller.signal.aborted) return;

      if (!freshUser || freshUser.isActive === false) {
        setState('unauthorized');
        return;
      }

      setUser(freshUser as Parameters<typeof setUser>[0]);
      lastValidAt = Date.now();
      setState('valid');
    } catch (err: unknown) {
      if (controller.signal.aborted) return;

      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        lastValidAt = 0;
        setUser(null);
        setState('unauthorized');
      } else if (status === 403) {
        setState('forbidden');
      } else {
        // 429, network error or 5xx: the session itself was not rejected, so
        // don't ask for the password again. Real API calls still enforce auth.
        setState('valid');
      }
    }
  }, [isAuthenticated, setUser]);

  useEffect(() => {
    validate();
    return () => abortRef.current?.abort();
  }, [validate, location.pathname]);

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
          <span className="text-sm text-muted-foreground/60">{t.loading}</span>
        </div>
      </div>
    );
  }

  if (state === 'forbidden') {
    return <SectionForbiddenScreen />;
  }

  if (state === 'unauthorized') {
    return <SectionInvalidScreen onReconnected={validate} />;
  }

  return <>{children}</>;
}
